import {isPassportIdentity} from '@sollapay/domain';
import {AssetType} from '@sollapay/enums';
import {
  composeIsraeliIban,
  dateToIso,
  generateNationalId,
  subtractDays,
  subtractYears
} from '@sollapay/utils';

import type {IdentityDocumentType} from '@sollapay/enums';

const CORP_NAME_WORDS = [
  'כרמל',
  'גליל',
  'דקל',
  'נוף',
  'שקד',
  'רימון',
  'תמר',
  'אבן',
  'זית',
  'אלון',
  'ברוש',
  'ורד',
  'הדר',
  'יער',
  'אגם'
];
const CORP_NAME_SUFFIXES = ['נדל״ן', 'בניה', 'יזום', 'השקעות', 'אחזקות'];
const PROJECT_NAMES = [
  'מגדלי הנהר',
  'פארק השרון',
  'גני הזית',
  'נאות השקד',
  'קריית הים',
  'מתחם הדקל',
  'שכונת האלון',
  'רובע הגליל',
  'מרכז הכרמל',
  'טיילת האגם'
];
const FIRST_NAMES = ['דוד', 'משה', 'יוסי', 'אבי', 'רונית', 'מיכל', 'שרה', 'נועה', 'איתן', 'טל'];
const LAST_NAMES = [
  'כהן',
  'לוי',
  'מזרחי',
  'פרץ',
  'ביטון',
  'אזולאי',
  'דיין',
  'שפירא',
  'אוחיון',
  'גבאי'
];
const STREET_NAMES = [
  'הרצל',
  'בן גוריון',
  'ויצמן',
  'רוטשילד',
  'אלנבי',
  'סוקולוב',
  'ז׳בוטינסקי',
  'ביאליק'
];
const CITY_NAMES = [
  'תל אביב',
  'ירושלים',
  'חיפה',
  'ראשון לציון',
  'פתח תקווה',
  'נתניה',
  'רמת גן',
  'הרצליה'
];
const PAYOUT_DESCRIPTIONS = [
  'תשלום למוכר על פי אבן דרך',
  'החזר כספי ללקוח',
  'תשלום סופי בגין עסקה',
  'העברה לספק שירותים'
];

const ASSET_DESCRIPTIONS_BY_TYPE: Record<AssetType, readonly string[]> = {
  [AssetType.DEVELOPER_UNIT]: [
    'יחידת מגורים חדשה מקבלן, טרם אוכלסה',
    'דירה בבניה עם מפרט טכני סטנדרטי',
    'יחידה בפרויקט חדש עם אפשרות לשינויי דיירים'
  ],
  [AssetType.APARTMENT]: [
    'דירת 4 חדרים משופצת עם מרפסת שמש',
    'דירה מרווחת בקומה גבוהה עם נוף פתוח',
    'דירת 3 חדרים קרובה למרכז העיר'
  ],
  [AssetType.PENTHOUSE]: ['פנטהאוז עם מרפסת גג ונוף פנורמי', 'פנטהאוז יוקרתי עם ג׳קוזי בגג'],
  [AssetType.GARDEN_APARTMENT]: [
    'יחידת גן עם גישה ישירה לחצר פרטית',
    'דירת גן משופצת עם גינה מטופחת'
  ],
  [AssetType.DUPLEX]: [
    'דופלקס דו קומתי עם חדרי שינה בקומה עליונה',
    'דופלקס מרווח עם מרפסת בקומה עליונה'
  ],
  [AssetType.STUDIO]: ['יחידת סטודיו קומפקטית במרכז העיר', 'סטודיו משופץ המתאים ליחיד או זוג'],
  [AssetType.HOUSE]: ['בית פרטי עם חצר וחניה פרטית', 'בית דו משפחתי עם כניסה נפרדת'],
  [AssetType.COTTAGE]: ['קוטג׳ צמוד קרקע עם גינה קדמית', 'קוטג׳ דו משפחתי במתחם שקט'],
  [AssetType.VILLA]: ['וילה מפוארת עם בריכה פרטית', 'וילה מרווחת עם גינה נופית וחניה מקורה'],
  [AssetType.BUILDING]: ['בניין מגורים בן מספר קומות', 'בניין משותף עם מעלית וחדר אשפה'],
  [AssetType.LAND]: ['מגרש פנוי המיועד לבניה למגורים', 'קרקע חקלאית בהליכי שינוי ייעוד'],
  [AssetType.LOT]: ['חלקת אדמה רשומה בטאבו', 'מגרש בגודל בינוני בשכונה מתפתחת'],
  [AssetType.COMMERCIAL]: ['שטח מסחרי בקומת קרקע עם חזית לרחוב', 'נכס מסחרי המתאים לעסק פעיל'],
  [AssetType.OFFICE]: ['משרד מרוהט בבניין משרדים מרכזי', 'חלל משרדים פתוח עם חדרי ישיבות'],
  [AssetType.STORE]: ['חנות ברחוב ראשי עם ויטרינת תצוגה', 'חנות קטנה במרכז מסחרי פעיל'],
  [AssetType.INDUSTRIAL]: ['שטח תעשייה עם גישה לרכב כבד', 'מבנה תעשייתי עם גובה תקרה גבוה'],
  [AssetType.WAREHOUSE]: ['מחסן לוגיסטי עם רמפת פריקה וטעינה', 'מחסן גדול עם גישה נוחה למשאיות'],
  [AssetType.PARKING]: ['חניה מקורה בחניון תת קרקעי', 'חניה פרטית צמודה לבניין'],
  [AssetType.STORAGE]: ['מחסן אחסון קטן בקומת מרתף', 'יחידת אחסון צמודה לדירה'],
  [AssetType.OTHER]: ['נכס לדוגמה לצורכי בדיקה', 'נכס כללי שאינו משויך לקטגוריה קבועה']
};

function pickRandom<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)] as T;
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomDigitString(length: number): string {
  return Array.from({length}, () => randomInt(0, 9)).join('');
}

export function generatePassportNumber(): string {
  return randomDigitString(8);
}

export function generateIdentityNumber(idType: IdentityDocumentType): string {
  return isPassportIdentity(idType) ? generatePassportNumber() : generateNationalId();
}

export function generateHebrewCorpName(): string {
  return `${pickRandom(CORP_NAME_WORDS)} ${pickRandom(CORP_NAME_SUFFIXES)} בע״מ`;
}

export function generateHebrewPersonName(): string {
  return `${pickRandom(FIRST_NAMES)} ${pickRandom(LAST_NAMES)}`;
}

/** Matches the modal's own placeholder shape: "עו״ד ישראל ישראלי עבור מגדלי הנהר". */
export function generateTrustAccountName(): string {
  return `עו״ד ${generateHebrewPersonName()} עבור ${pickRandom(PROJECT_NAMES)}`;
}

export function generateIsraeliAddress(): string {
  const houseNumber = Math.floor(Math.random() * 150) + 1;
  return `${pickRandom(STREET_NAMES)} ${houseNumber}, ${pickRandom(CITY_NAMES)}`;
}

export function generateSmallNumber(): string {
  return String(randomInt(1, 40));
}

/** Matches `AREA_PATTERN` (`@sollapay/domain/asset-types`) — up to 2 decimal places. */
export function generateArea(): string {
  return `${randomInt(35, 220)}.${randomInt(0, 9)}${randomInt(0, 9)}`;
}

export function generateAssetDescription(assetType: AssetType): string {
  return pickRandom(ASSET_DESCRIPTIONS_BY_TYPE[assetType]);
}

export function generatePurchasePriceNis(): string {
  return String(randomInt(500_000, 3_000_000));
}

/** A trust account's expected aggregate turnover — same order of magnitude as a single
 * asset's price, but a distinct concept, so it gets its own generator rather than reusing
 * `generatePurchasePriceNis`. */
export function generateTrustVolumeNis(): string {
  return String(randomInt(500_000, 3_000_000));
}

const MOBILE_PREFIXES = ['050', '052', '053', '054', '055', '058'];

export function generatePhoneNumber(): string {
  return `${pickRandom(MOBILE_PREFIXES)}${randomDigitString(7)}`;
}

function randomPastDate(minYearsAgo: number, maxYearsAgo: number): string {
  const date = subtractDays(
    subtractYears(new Date(), randomInt(minYearsAgo, maxYearsAgo)),
    randomInt(0, 364)
  );
  return dateToIso(date);
}

export function generateDateOfBirth(): string {
  return randomPastDate(25, 70);
}

export function generateIncorporationDate(): string {
  return randomPastDate(1, 40);
}

export function generatePayoutAmount(): string {
  return String(randomInt(1_000, 500_000));
}

export function generatePayoutDescription(): string {
  return pickRandom(PAYOUT_DESCRIPTIONS);
}

export function generateIsraeliIban(): string {
  return composeIsraeliIban({
    bankCode: String(randomInt(10, 99)),
    branchCode: String(randomInt(100, 999)),
    accountNumber: String(randomInt(1_000_000, 99_999_999))
  });
}

export function generateBankAccountNumber(): string {
  return String(randomInt(100_000, 9_999_999));
}
